import { useParams } from 'react-router-dom'
import PageHeader from '../../components/layout/PageHeader'
import Location from '../../hooks/Location'
import { isSuperAdminTenant } from '../../utils/superAdminTenant'
import useMaterialOutputBenchmark from '../../hooks/useMaterialOutputBenchmark'
import { MATERIAL_OUTPUTS, NIVEAU_OUTPUT, formatBenchmarkValue } from '../../helpers/dashboard/buildBenchmarkRows'
import { sectorContextQuestions } from '../../helpers/dataHelpers/sectorContextQuestions'
import buildMaterialContextRows from '../../helpers/intelligence/buildMaterialContextRows'
import MaterialContextDotPlot from '../../components/intelligence/MaterialContextDotPlot'
import telwoord from '../../helpers/intelligence/telwoord'

/**
 * Eén materialen-benchmark (bespaarde CO2 of besparing op de energierekening),
 * uitgesplitst naar de contextkenmerken die energiearmoede-organisaties invullen
 * bij hun Theory of Change ('Context — Kenmerken van je organisatie').
 *
 * De uitsplitsing rekent de server uit en stuurt hij alleen naar de
 * beheertenant: een groepje van één organisatie verraadt anders haar niveau en
 * haar antwoorden. Deze pagina zet er alleen labels en volgorde bij.
 *
 * AI-CONTEXT: See functions/materialsHelpers/buildMaterialContextSplit.js
 * AI-CONTEXT: See docs/features/intelligence-thema-eerst.md
 */
const MaterialBenchmarkDetailContent = () => {
    const { output: segment } = useParams()
    // Alleen een bekende output is een sleutel; 'undefined' uit de URL is
    // truthy en zou anders als output opgevraagd worden.
    // AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
    const meta = MATERIAL_OUTPUTS.find((o) => o.output === segment) || null
    const output = meta ? meta.output : ''
    const { benchmark, error } = useMaterialOutputBenchmark(output, Boolean(meta))

    /** @param {number|null} level */
    const waarde = (level) => formatBenchmarkValue({ kind: NIVEAU_OUTPUT, value: level, unit: meta ? meta.unit : '' })

    if (!meta) {
        return (
            <div className="main"><div className="main-container">
                <p className="intelligence-state">Onbekende materialen-benchmark.</p>
            </div></div>
        )
    }

    const inhoud = () => {
        if (error) {
            return (
                <p className="intelligence-state intelligence-state--error">
                    De benchmark kon niet worden geladen: {error.message}
                </p>
            )
        }
        // De hook begint zonder benchmark en zonder fout; dat is 'nog niet
        // bekend', geen lege benchmark.
        // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
        if (!benchmark) return <p className="intelligence-state">Benchmark laden…</p>

        const split = benchmark.contextSplit
        const secties = buildMaterialContextRows(split, sectorContextQuestions.energiearmoede.groups)

        return (
            <>
                <div className="intelligence-panel">
                    <div className="intelligence-agenda-row">
                        <span className="intelligence-agenda-row__body">
                            <span className="intelligence-agenda-row__stand">
                                {telwoord(benchmark.companyCount, 'organisatie', 'organisaties')},{' '}
                                {telwoord(benchmark.householdCount, 'huishouden', 'huishoudens')}
                            </span>
                        </span>
                        <span className="intelligence-benchmark__waarde">{waarde(benchmark.benchmarkLevel)}</span>
                    </div>
                </div>

                {/* Een oudere versie van de functie stuurt geen uitsplitsing.
                    Dat is iets anders dan 'geen kenmerken'. */}
                {!split && (
                    <p className="intelligence-state intelligence-state--error">
                        De server stuurde geen uitsplitsing naar contextkenmerken mee.
                    </p>
                )}
                {split && split.companyCount === 0 && (
                    <p className="intelligence-state">
                        Er zitten geen energiearmoede-organisaties in deze benchmark.
                    </p>
                )}
                {split && split.companyCount > 0 && (
                    <>
                        <section className="intelligence-panel" aria-labelledby="materiaal-context-basis">
                            <h3 id="materiaal-context-basis" className="intelligence-panel__title">Energiearmoede-organisaties</h3>
                            <div className="intelligence-agenda-row">
                                <span className="intelligence-agenda-row__body">
                                    <span className="intelligence-agenda-row__stand">
                                        {telwoord(split.companyCount, 'energiearmoede-organisatie', 'energiearmoede-organisaties')},{' '}
                                        {telwoord(split.householdCount, 'huishouden', 'huishoudens')}
                                    </span>
                                </span>
                                <span className="intelligence-benchmark__waarde">{waarde(split.benchmarkLevel)}</span>
                            </div>
                        </section>

                        {/* Het overzicht in één beeld; de tabellen eronder
                            houden de exacte cijfers en zijn de tabelweergave. */}
                        <div className="intelligence-panel">
                            <MaterialContextDotPlot
                                sections={secties}
                                reference={split.benchmarkLevel}
                                unit={meta.shortUnit}
                            />
                        </div>

                        {secties.map((sectie) => (
                            <section
                                key={sectie.id}
                                className="intelligence-panel"
                                aria-labelledby={`materiaal-context-${sectie.id}`}
                            >
                                <h3 id={`materiaal-context-${sectie.id}`} className="intelligence-panel__title">
                                    {sectie.label}
                                </h3>
                                <table className="sector-insight-details-table">
                                    <thead>
                                        <tr>
                                            <th>Antwoord</th>
                                            <th>Organisaties</th>
                                            <th>Huishoudens</th>
                                            <th>Per huishouden</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sectie.rows.map((rij) => (
                                            <tr key={rij.key}>
                                                <td>{rij.label}</td>
                                                <td>{rij.companyCount}</td>
                                                <td>{rij.householdCount}</td>
                                                <td>
                                                    {waarde(rij.benchmarkLevel)}
                                                    {rij.tooFew && (
                                                        <span className="intelligence-material-context__few">
                                                            te weinig om te vergelijken
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        ))}
                    </>
                )}
            </>
        )
    }

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader heading={meta.effectName} AI={false} />
                {inhoud()}
            </div>
        </div>
    )
}

/**
 * De poort staat om de inhoud heen: zo draait er geen aanroep voor wie er niet
 * hoort te zijn. De echte poort is server-side (de uitsplitsing gaat alleen
 * naar de beheertenant).
 * AI-CONTEXT: See docs/pitfalls.md#security-cf-admin-tenant-bypass
 */
const MaterialBenchmarkDetail = () => {
    const client = Location()[1]

    if (!isSuperAdminTenant(client)) {
        return (
            <div className="main">
                <div className="main-container">
                    <p className="intelligence-state">
                        Deze pagina is alleen beschikbaar voor het Deccos-beheerteam.
                    </p>
                </div>
            </div>
        )
    }

    return <MaterialBenchmarkDetailContent />
}

export default MaterialBenchmarkDetail
